import classNames from 'classnames';
import { format } from 'date-fns';
import React from 'react';

import { TemplateSchema } from '@Components/forms/SchemaFields/SchemaFields';
import GridCol from '@Components/layout/Grid/GridCol';
import Row from '@Components/layout/Row';
import Typography from '@Components/Typography';
import { TimeFormat } from '@Config/constants';
import { useGetCategoriesQuery } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { isDefined } from '@Utils/tools';
import { MessageKeysWithoutParams } from '@Utils/translation';

import styles from './PropertiesTable.module.scss';

type PropertiesTableProps = {
  categoryId: string;
  customFieldsData: string;
};

type TableDataItem = { msgKey: MessageKeysWithoutParams; value: string; format: string };

const transformData = ({ schema, data }: { schema: TemplateSchema; data: Record<string, string> }) => {
  const result = {} as Record<string, TableDataItem>;

  Object.keys(data).forEach(key => {
    const property = schema.properties[key];
    if (!property) {
      return;
    }

    let value = data[key];

    if (property.options) {
      const optionFound = property.options.find(option => option.value === value);

      if (optionFound) {
        value = optionFound.msgKey;
      }
    }
    result[key] = {
      msgKey: property.titleMsgKey,
      value,
      format: property.format,
    };
  });

  return result;
};

const PropertiesTable = ({ categoryId, customFieldsData }: PropertiesTableProps) => {
  const { data: categoriesFields } = useGetCategoriesQuery();
  const t = useTranslations();

  const categorySelected = categoriesFields?.categories.find(category => {
    return category.id === categoryId;
  });

  if (
    !isDefined(customFieldsData) ||
    !isDefined(categorySelected) ||
    !categorySelected?.customFieldsSchema ||
    !('properties' in JSON.parse(categorySelected?.customFieldsSchema))
  ) {
    return null;
  }

  const categorySchema = JSON.parse(categorySelected?.customFieldsSchema);
  const advertCustomFields = JSON.parse(customFieldsData);
  const data = transformData({ schema: categorySchema, data: advertCustomFields });
  const tableRows = Object.values(data) as TableDataItem[];

  return (
    <GridCol size={7} className={styles.container}>
      {tableRows.map((item, index) => (
        <Row
          constant
          key={item.msgKey}
          justifyBetween
          className={classNames(styles.tableRow, tableRows.length - 1 !== index && styles.separator)}
        >
          <Typography msg={t(item.msgKey)} tag="span" />
          <Typography
            msg={item.format === 'date' ? format(new Date(item.value), TimeFormat.year) : item.value}
            tag="span"
          />
        </Row>
      ))}
    </GridCol>
  );
};

export default PropertiesTable;
